import { filter, get, omit } from 'lodash';
import Divider from 'material-ui/Divider';
import EventNote from 'material-ui/svg-icons/notification/event-note';
import * as React from 'react';
import { Col, Grid, Row } from 'react-flexbox-grid';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withProps, withState } from 'recompose';
import { formValueSelector, change as reduxChange, reduxForm, stopSubmit, touch } from 'redux-form';
import { actions as notifyActions } from 'redux-notifications';
import * as uuid from 'uuid/v4';

import Button from '@inqool/uas-web-core/es/components/controls/Button';

import HelpTooltip from '@components/HelpTooltip/HelpTooltip';

import {
  AutoComplete,
  AutoCompleteField,
  Date,
  DateField,
  Input,
  InputField,
  NumberSequence,
  RadioSwitch,
  RadioSwitchField,
  RemoteAutoComplete,
  RemoteAutoCompleteField,
  RemoteAutoCompleteTree,
  RemoteAutoCompleteTreeField,
  Toggle,
  ToggleField,
  ToggleTwoLabels
} from '@components/form';
import Address, { AddressField } from '@components/form/Address/Address';
import { withNotifications } from '@components/hoc/withNotifications';
import { Loader } from '@components/loader';
import { putDelict, undoFromDelict } from '@modules/delicts/_delictsActions';
import { EventsFormDelictsAgreementForm } from '@modules/events/EventsFormDelictsAgreementForm';
import { EventsFormDelictsBNPPaymentForm } from '@modules/events/EventsFormDelictsBNPPaymentForm';
import { EventsFormDelictsBNZPaymentForm } from '@modules/events/EventsFormDelictsBNZPaymentForm';
import { EventsFormDelictsForwardedSoForm } from '@modules/events/EventsFormDelictsForwardedSoForm';
import { EventsFormDelictsNotHappendForm } from '@modules/events/EventsFormDelictsNotHappendForm';
import { EventsFormDelictsPaymentForm } from '@modules/events/EventsFormDelictsPaymentForm';
import { EventsFormDelictsSaveISEPForm } from '@modules/events/EventsFormDelictsSaveISEPForm';
import { getIsepFile, getIsrtFile } from '@modules/events/_eventsActions';
import { delictResolutionEnum, delictStateEnum } from '@modules/events/_eventsEnums';
import { paymentMethod, ticketState } from '@modules/tickets/_ticketsEnums';
import { downloadFile } from '@utils/actions';
import { getActualDate, hasAnyPermission } from '@utils/utils';
import { required } from '@utils/validators';
import { Dialog, IconButton } from 'material-ui';

const enhance = compose<any, any>(
  withProps(({ initialValues, event }) => ({
    initialValues: {
      event,
      intention: false,
      towPayedNoticeOfficer: !!get(initialValues, 'noticeOfficer', null),
      date: getActualDate(),

      ...initialValues
    }
  })),
  withNotifications({ getIsepFile, getIsrtFile, undoFromDelict, putDelict }),
  reduxForm({
    form: 'module-events-delicts',
    keepDirtyOnReinitialize: true,
    enableReinitialize: true
  }),
  connect(state => ({
    participants: formValueSelector('module-events')(state, 'participants'),
    vehicles: formValueSelector('module-events')(state, 'vehicles'),
    eventAddress: formValueSelector('module-events')(state, 'address'),

    resolution: formValueSelector('module-events-delicts')(state, 'resolution'),
    fineState: formValueSelector('module-events-delicts')(state, 'fine.state'),
    delictId: formValueSelector('module-events-delicts')(state, 'id'),
    ticketId: formValueSelector('module-events-delicts')(state, 'fine.id'),
    offenderId: formValueSelector('module-events-delicts')(state, 'offender.id'),
    offenderFirstName: formValueSelector('module-events-delicts')(state, 'offender.person.firstName'),
    offenderLastName: formValueSelector('module-events-delicts')(state, 'offender.person.lastName'),
    offenderCitizenship: formValueSelector('module-events-delicts')(state, 'offender.person.citizenship.a2'),
    offenderBirthAddress: formValueSelector('module-events-delicts')(state, 'offender.person.birthAddress.id'),
    offenderBirthDate: formValueSelector('module-events-delicts')(state, 'offender.person.birthDate'),
    offenderBirthNumber: formValueSelector('module-events-delicts')(state, 'offender.person.birthNumber'),
    noticeOfficer: formValueSelector('module-events-delicts')(state, 'noticeOfficer'),
    noticeTicket: formValueSelector('module-events-delicts')(state, 'noticeTicket'),
    towPayedNoticeOfficer: formValueSelector('module-events-delicts')(state, 'towPayedNoticeOfficer'),
    delictState: formValueSelector('module-events-delicts')(state, 'state'),
    isepId: formValueSelector('module-events-delicts')(state, 'isepId'),
    isepCheckDatetime: formValueSelector('module-events-delicts')(state, 'isepCheckDatetime'),
    resolutionDate: formValueSelector('module-events-delicts')(state, 'resolutionDate'),
    fineCheck: formValueSelector('module-events-delicts')(state, 'fine.check')
  })),
  withState<{}, boolean, string, string>('loader', 'handleLoader', false),
  withState<{}, boolean, string, string>('showSaveISEP', 'handleShowSaveISEP', false),
  withState<{}, boolean, string, string>('showAgreement', 'handleShowAgreement', false),
  withState<{}, boolean, string, string>('showPayment', 'handleShowPayment', false),
  withState<{}, boolean, string, string>('showBNP', 'handleShowBNP', false),
  withState<{}, boolean, string, string>('showBNPPayment', 'handleShowBNPPayment', false),
  withState<{}, boolean, string, string>('showBNZPayment', 'handleShowBNZPayment', false),
  withState<{}, boolean, string, string>('showNotHappend', 'handleShowNotHappend', false),
  withState<{}, boolean, string, string>('showForwardedSo', 'handleShowForwardedSo', false),
  withHandlers({
    onSubmit: ({ reloadEvent, initialize, event, putDelictNotify, handleLoader }) => async formData => {
      handleLoader(true);
      const body = { id: get(formData, 'id', uuid()), event, ...formData };
      const result = await putDelictNotify(body);
      if (result) {
        const newEvent = await reloadEvent();
        const newDelict = get(newEvent, 'delicts', []).find(d => get(d, 'id') === get(result, 'id'));

        const newDelictwithTowPayedNoticeOfficer = {
          ...newDelict,
          towPayedNoticeOfficer: !!get(newDelict, 'noticeOfficer', null)
        };
        initialize(newDelictwithTowPayedNoticeOfficer);
      }
      handleLoader(false);
    },
    reloadDelict: ({ reloadEvent, initialize, delictId }) => async () => {
      const event = await reloadEvent();
      const newDelict = get(event, 'delicts', []).find(d => get(d, 'id') === delictId);
      initialize(newDelict);
    }
  }),
  withHandlers({
    handleIsepFile: ({
      delictId,
      offenderId,
      offenderFirstName,
      offenderLastName,
      getIsepFileNotify,
      handleLoader,
      reloadDelict
    }) => async () => {
      handleLoader(true);
      const isepFileId = await getIsepFileNotify(delictId, offenderId);
      if (isepFileId) {
        await downloadFile(isepFileId, `ISEP-${offenderLastName}-${offenderFirstName}.pdf`);
        await reloadDelict();
      }
      handleLoader(false);
    },
    handleIsrtFile: ({
      delictId,
      offenderId,
      offenderFirstName,
      offenderLastName,
      offenderCitizenship,
      offenderBirthAddress,
      offenderBirthDate,
      offenderBirthNumber,
      getIsrtFileNotify,
      handleLoader,
      reloadDelict,
      addError
    }) => async () => {
      if (!offenderBirthAddress) {
        addError('Pachatel musí mít vyplněnou adresu narození');
        return;
      }
      if (offenderCitizenship === 'CZ' && !offenderBirthNumber) {
        addError('Pachatel s českým občanstvím musí mít vyplněné rodné číslo');
        return;
      }
      if (offenderCitizenship !== 'CZ' && !offenderBirthDate) {
        addError('Pachatel bez českého občanství musí mít vyplněný datum narození');
        return;
      }

      handleLoader(true);
      const isrtResponse = await getIsrtFileNotify(delictId, offenderId);
      if (isrtResponse.state === 'COMPLETED') {
        await downloadFile(isrtResponse.files[0].id, `ISRT-${offenderLastName}-${offenderFirstName}.pdf`);
        await reloadDelict();
      } else if (isrtResponse.state === 'PROCESSING') {
        addError('ISRT spracovává žádost, skuste to později');
      } else if (isrtResponse.state === 'ERROR') {
        addError('Chyba při voláni ISRT');
      }

      handleLoader(false);
    },
    undo: ({ reloadDelict, delictId, handleLoader, undoFromDelictNotify }) => async () => {
      handleLoader(true);
      await undoFromDelictNotify(delictId);
      await reloadDelict();
      handleLoader(false);
    },
    isResolutionValid: ({ dispatch, resolutionDate }) => () => {
      if (!resolutionDate) {
        dispatch(touch('module-events-delicts', 'resolutionDate'));
        dispatch(
          stopSubmit('module-events-delicts', {
            resolutionDate: 'Povinné pro vyřešení přestupku'
          } as any)
        );
        dispatch(
          notifyActions.notifSend({
            message: 'Pro vyřešení přestupku vyplňte datum a čas vyřešení přestupku',
            kind: 'danger',
            dismissAfter: 6000
          })
        );
        return false;
      }
      return true;
    }
  }),
  withProps(({ participants = [], dispatch, delictState }: any) => ({
    participants: filter(participants, ({ type }) => type === 'PERPETRATOR'),
    change: (field, value) => dispatch(reduxChange('module-events-delicts', field, value)),
    disabled: delictState && delictState !== 'CREATED',
    undoPermission: hasAnyPermission(['ROLE_DELICT_UNDO'])
  })),
  lifecycle<any, any>({
    componentDidUpdate() {
      const { noticeOfficer, towPayedNoticeOfficer, change } = this.props;
      if (!!noticeOfficer && !towPayedNoticeOfficer) {
        change('towPayedNoticeOfficer', true);
      }
    }
  })
);

const View: React.FC<any> = ({
  handleSubmit,
  closeItem,
  change,
  participants,
  vehicles,
  showAgreement,
  handleShowSaveISEP,
  showSaveISEP,
  handleShowAgreement,
  showPayment,
  handleShowPayment,
  showBNP,
  handleShowBNP,
  delictId,
  showBNPPayment,
  handleShowBNPPayment,
  showBNZPayment,
  handleShowBNZPayment,
  showNotHappend,
  handleShowNotHappend,
  showForwardedSo,
  handleShowForwardedSo,
  ticketId,
  dirty,
  reloadDelict,
  resolution,
  fineState,
  onSubmit,
  offenderId,
  handleIsepFile,
  handleIsrtFile,
  loader,
  towPayedNoticeOfficer,
  disabled,
  eventDefaultPlace,
  isepId,
  isepCheckDatetime,
  undo,
  undoPermission,
  isResolutionValid,
  noticeOfficer,
  noticeTicket,
  eventAddress,
  context,
  fineCheck
}) => {
  const buttonDisabled = !delictId || !offenderId;
  const buttonOptionalOffender = !delictId;
  const withTreeTypes = get(context, 'config.extensions.delict-types-tree') === 'true';

  return (
    <div>
      {loader && <Loader />}
      <Dialog
        bodyStyle={{ padding: '0', maxWidth: '75vw' }}
        open={showSaveISEP}
        onRequestClose={() => handleShowSaveISEP(false)}
      >
        <EventsFormDelictsSaveISEPForm
          closeItem={() => handleShowSaveISEP(false)}
          reloadDelict={reloadDelict}
          delictId={delictId}
        />
      </Dialog>
      <Dialog bodyStyle={{ padding: '0' }} open={showAgreement} onRequestClose={() => handleShowAgreement(false)}>
        <EventsFormDelictsAgreementForm
          closeItem={() => handleShowAgreement(false)}
          reloadDelict={reloadDelict}
          delictId={delictId}
        />
      </Dialog>
      <Dialog bodyStyle={{ padding: '0' }} open={showPayment} onRequestClose={() => handleShowPayment(false)}>
        <EventsFormDelictsPaymentForm
          closeItem={() => handleShowPayment(false)}
          isBNP={false}
          reloadDelict={reloadDelict}
          delictId={delictId}
        />
      </Dialog>
      <Dialog bodyStyle={{ padding: '0' }} open={showBNP} onRequestClose={() => handleShowBNP(false)}>
        <EventsFormDelictsPaymentForm
          closeItem={() => handleShowBNP(false)}
          isBNP={true}
          reloadDelict={reloadDelict}
          delictId={delictId}
        />
      </Dialog>
      <Dialog bodyStyle={{ padding: '0' }} open={showBNPPayment} onRequestClose={() => handleShowBNPPayment(false)}>
        <EventsFormDelictsBNPPaymentForm
          closeItem={() => handleShowBNPPayment(false)}
          reloadDelict={reloadDelict}
          ticketId={ticketId}
        />
      </Dialog>
      <Dialog bodyStyle={{ padding: '0' }} open={showBNZPayment} onRequestClose={() => handleShowBNZPayment(false)}>
        <EventsFormDelictsBNZPaymentForm
          closeItem={() => handleShowBNZPayment(false)}
          reloadDelict={reloadDelict}
          ticketId={ticketId}
        />
      </Dialog>
      <Dialog bodyStyle={{ padding: '0' }} open={showNotHappend} onRequestClose={() => handleShowNotHappend(false)}>
        <EventsFormDelictsNotHappendForm
          closeItem={() => handleShowNotHappend(false)}
          reloadDelict={reloadDelict}
          delictId={delictId}
        />
      </Dialog>
      <Dialog bodyStyle={{ padding: '0' }} open={showForwardedSo} onRequestClose={() => handleShowForwardedSo(false)}>
        <EventsFormDelictsForwardedSoForm
          closeItem={() => handleShowForwardedSo(false)}
          reloadDelict={reloadDelict}
          delictId={delictId}
        />
      </Dialog>
      <form className="layout" onSubmit={handleSubmit}>
        <Grid fluid={true}>
          <Row>
            <Col className="grid-system-margin" />
          </Row>

          <Row>
            <Col md={12}>
              <Row>
                <Col md={12}>
                  <h4>Základní informace</h4>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <InputField
                    component={Input}
                    name={'isepId'}
                    label={'Identifikátor v systému ISEP'}
                    disabled={true}
                  />
                </Col>
                <Col md={3}>
                  <NumberSequence
                    inputName={'number'}
                    label={'Sekvenční číslo *'}
                    formName={'module-events-delicts'}
                    sequenceKey={'sequenceDelict'}
                    inputProps={{ disabled: true }}
                    validate={[required]}
                  />
                </Col>
                <Col md={6}>
                  <AddressField
                    component={Address}
                    name={'address'}
                    label={'Adresa přestupku'}
                    disabled={disabled}
                    defaultPlace={eventDefaultPlace}
                    MiddleComponent={() => (
                      <IconButton
                        tooltip={<span style={{ whiteSpace: 'normal' }}>předvyplnění adresy z údalosti</span>}
                        onClick={() => change('address', omit(eventAddress, 'id'))}
                      >
                        <EventNote />
                      </IconButton>
                    )}
                  />
                </Col>
                <Col md={12}>
                  <InputField
                    component={Input}
                    name={'description'}
                    label={'Popis skutku'}
                    disabled={disabled}
                    multiLine={true}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Row>
                <Col md={12}>
                  <h4>Přestupce</h4>
                </Col>
              </Row>
              <Row>
                {[
                  {
                    name: 'offender',
                    label: 'Přestupce *',
                    items: participants,
                    labelProp: 'person.fullName'
                  },
                  {
                    name: 'vehicle',
                    label: 'Vozidlo přestupce',
                    items: vehicles,
                    labelProp: 'registrationNumber'
                  }
                ].map(({ name, label, items, labelProp, ...rest }) => (
                  <Col md={4} key={name}>
                    <AutoCompleteField
                      component={AutoComplete}
                      name={name}
                      label={label}
                      items={items}
                      fullWidth={true}
                      labelFunction={o => get(o, labelProp)}
                      valueFunction={o => o}
                      disabled={disabled}
                      {...rest}
                    />
                  </Col>
                ))}
                <Col md={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <RadioSwitchField
                    component={RadioSwitch}
                    name="intention"
                    label1="Úmysl"
                    label2="Nedbalost"
                    onChange={(_, value) => {
                      change('intention', value);
                    }}
                    disabled={disabled}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Row>
                <Col md={12}>
                  <h4>Data</h4>
                </Col>
              </Row>
              <Row>
                {[
                  { name: 'date', label: 'Datum a čas přestupku', validate: [required] },
                  { name: 'resolutionDate', label: 'Datum a čas vyřešení *' },
                  { name: 'closeDate', label: 'Datum a čas uzavření' }
                ].map(field => (
                  <Col md={4} key={field.name}>
                    <DateField component={Date} dateOnly={false} disabled={disabled} {...field} />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Row>
                <Col md={12}>
                  <h4>Řešení</h4>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  {withTreeTypes ? (
                    <RemoteAutoCompleteTreeField
                      component={RemoteAutoCompleteTree}
                      id={'type'}
                      change={change}
                      formName={'module-events-delicts'}
                      name={'type'}
                      label={'Typ přestupku'}
                      itemLabel={'name'}
                      disabled={disabled}
                      url={`/metropolis/api/ed/delict/types`}
                      fullWidth={true}
                      labelFunction={o => get(o, 'fullName')}
                      valueFunction={o => ({
                        id: get(o, 'id')
                      })}
                      validateValue={[required]}
                    />
                  ) : (
                    <RemoteAutoCompleteField
                      component={RemoteAutoComplete}
                      filterActive={true}
                      fullWidth={true}
                      disabled={disabled}
                      name={'type'}
                      label={'Typ přestupku *'}
                      url={'/metropolis/api/ed/delict/types'}
                      labelFunction={o => get(o, 'fullName')}
                      validate={[required]}
                    />
                  )}
                </Col>
                <Col md={6}>
                  <RemoteAutoCompleteField
                    component={RemoteAutoComplete}
                    filterActive={true}
                    fullWidth={true}
                    disabled={disabled}
                    name={'discovery'}
                    label={'Způsob zjištění'}
                    url={'/metropolis/api/ed/delict/discoveries'}
                    labelFunction={o => get(o, 'name')}
                  />
                </Col>
              </Row>
              <Row>
                {[
                  { name: 'state', label: 'Stav', labelProp: 'label', items: delictStateEnum },
                  { name: 'resolution', label: 'Způsob řešení', labelProp: 'label', items: delictResolutionEnum }
                ].map(field => (
                  <Col md={6} key={field.name}>
                    <AutoCompleteField
                      component={AutoComplete}
                      name={field.name}
                      label={field.label}
                      fullWidth={true}
                      items={field.items}
                      disabled={true}
                      labelFunction={o => get(o, field.labelProp)}
                      valueFunction={o => get(o, 'id')}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>

          <Row>
            <Col md={3} className="with-right-divider" style={{ display: 'flex', flexDirection: 'column' }}>
              <Row>
                <Col md={12}>
                  <h4>Botička</h4>
                </Col>
              </Row>
              <Row style={{ flexGrow: 1 }}>
                <Col md={12} style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                  <ToggleField
                    component={Toggle}
                    style={{ margin: '10px 0' }}
                    name="clampPayed"
                    label="Nasazená"
                    labelPosition="right"
                    onToggle={(_, value) => {
                      change('clampPayed', value);
                    }}
                    disabled={disabled}
                  />
                </Col>
              </Row>
            </Col>
            <Col md={9}>
              <Row>
                <Col md={12}>
                  <div style={{ margin: '1.33em', fontWeight: 'bold' }}>
                    <ToggleTwoLabels
                      component={Toggle}
                      name="towPayedNoticeOfficer"
                      onToggle={(_, value) => {
                        if (value) {
                          change('noticeDate', getActualDate());
                          change('towPayed', false);
                        } else {
                          change('noticeDate', null);
                          change('noticeOfficer', null);
                          change('noticeTicket', null);
                        }
                        change('towPayedNoticeOfficer', value);
                      }}
                      labelLeft="Odtah"
                      labelRight="Vyrozumění"
                      onClickLeft={() => change('towPayedNoticeOfficer', false)}
                      onClickRight={() => change('towPayedNoticeOfficer', true)}
                      disabled={disabled}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={4} style={{ display: 'flex', alignItems: 'center' }}>
                  <ToggleField
                    component={Toggle}
                    style={{ margin: '10px 0' }}
                    name="towPayed"
                    label="Provedeno"
                    labelPosition="right"
                    onToggle={(_, value) => {
                      change('towPayed', value);
                    }}
                    disabled={disabled || towPayedNoticeOfficer}
                  />
                </Col>
                <Col md={8}>
                  <Row>
                    <Col md={6}>
                      <RemoteAutoCompleteField
                        component={RemoteAutoComplete}
                        id="noticeOfficer"
                        name="noticeOfficer"
                        label="Vyrozumění"
                        url="/metropolis/api/hr/employees"
                        labelFunction={o => get(o, 'fullName')}
                        fullWidth={true}
                        disabled={disabled || !towPayedNoticeOfficer}
                      />
                    </Col>
                    <Col md={6}>
                      <DateField
                        component={Date}
                        name={'noticeDate'}
                        label={'Datum a čas vyrozumění'}
                        dateOnly={false}
                        disabled={disabled || !towPayedNoticeOfficer}
                      />
                    </Col>
                    <Col md={12}>
                      <RemoteAutoCompleteField
                        component={RemoteAutoComplete}
                        name={'noticeTicket'}
                        label="Stěračenka"
                        url={'/metropolis/api/ed/tickets'}
                        disabled={disabled || !towPayedNoticeOfficer || !get(noticeOfficer, 'id')}
                        params={{
                          sort: 'intNumber',
                          order: 'ASC',
                          filter: [
                            {
                              field: 'type.id',
                              operation: 'EQ',
                              value: 'NT'
                            },
                            {
                              field: 'employee.id',
                              operation: 'EQ',
                              value: get(noticeOfficer, 'id', 'without-id')
                            },
                            {
                              field: '',
                              operation: 'OR',
                              filter: [
                                {
                                  field: 'state.id',
                                  operation: 'EQ',
                                  value: 'ISSUED'
                                },
                                {
                                  field: 'id',
                                  operation: 'EQ',
                                  value: get(noticeTicket, 'id', 'without-ticket')
                                }
                              ]
                            }
                          ]
                        }}
                        fullWidth={true}
                        labelFunction={o => get(o, 'fullNumber')}
                        valueFunction={o => o}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>

          <Divider />

          <Row>
            <Col md={12}>
              <Row>
                <Col md={12}>
                  <h4>Zákon</h4>
                </Col>
              </Row>
              <Row>
                {[
                  { name: 'type.law', label: 'Zákon', colSize: 6 },
                  { name: 'type.amendment', label: 'Novela', colSize: 6 },
                  { name: 'type.paragraph', label: 'Paragraf', colSize: 3 },
                  { name: 'type.section', label: 'Odstavec', colSize: 3 },
                  { name: 'type.letter', label: 'Písmeno', colSize: 3 },
                  { name: 'type.pointOfLetter', label: 'Bod', closeItem: 3 }
                ].map(field => (
                  <Col md={field.colSize} key={field.name}>
                    <InputField component={Input} name={field.name} label={field.label} disabled={true} />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>

          {resolution &&
            resolution === 'BNP' && (
              <>
                <Row>
                  <Col md={12}>
                    <h4>BNP</h4>
                  </Col>
                  <Col md={6}>
                    <RemoteAutoCompleteField
                      component={RemoteAutoComplete}
                      name={'fine.employee'}
                      label="Zaměstnanec"
                      url={`/metropolis/api/hr/employees`}
                      fullWidth={true}
                      labelFunction={o => get(o, 'fullName')}
                      valueFunction={o => o}
                      disabled={true}
                    />
                  </Col>
                  <Col md={6}>
                    <InputField component={Input} name="fine.fullNumber" label="Doklad" disabled={true} />
                  </Col>
                  <Col md={4}>
                    <AutoCompleteField
                      component={AutoComplete}
                      name="fine.state"
                      label="Stav dokladu"
                      fullWidth={true}
                      items={ticketState}
                      labelFunction={o => get(o, 'label')}
                      valueFunction={o => get(o, 'id')}
                      disabled={true}
                    />
                  </Col>
                  <Col md={4}>
                    <InputField component={Input} name={'fine.value'} label={'Suma'} disabled={true} />
                  </Col>
                  <Col md={4}>
                    <DateField
                      component={Date}
                      name={'fine.useDate'}
                      label={'Datum užití'}
                      dateOnly={false}
                      disabled={true}
                    />
                  </Col>
                </Row>
                {fineCheck && (
                  <Row>
                    <Col md={12}>
                      <h4>Platba</h4>
                    </Col>
                    <Col md={6}>
                      <RemoteAutoCompleteField
                        component={RemoteAutoComplete}
                        name={'fine.check.employee'}
                        label="Zaměstnanec"
                        url={`/metropolis/api/hr/employees`}
                        fullWidth={true}
                        labelFunction={o => get(o, 'fullName')}
                        valueFunction={o => o}
                        disabled={true}
                      />
                    </Col>
                    <Col md={6}>
                      <InputField component={Input} name="fine.check.fullNumber" label="Doklad" disabled={true} />
                    </Col>
                    <Col md={4}>
                      <AutoCompleteField
                        component={AutoComplete}
                        name="fine.check.state"
                        label="Stav dokladu"
                        fullWidth={true}
                        items={ticketState}
                        labelFunction={o => get(o, 'label')}
                        valueFunction={o => get(o, 'id')}
                        disabled={true}
                      />
                    </Col>
                    <Col md={4}>
                      <AutoCompleteField
                        component={AutoComplete}
                        name="fine.check.paymentMethod"
                        label="Platební metoda"
                        fullWidth={true}
                        items={paymentMethod}
                        labelFunction={o => get(o, 'label')}
                        valueFunction={o => get(o, 'id')}
                        disabled={true}
                      />
                    </Col>
                    <Col md={4}>
                      <DateField
                        component={Date}
                        name={'fine.check.collectDate'}
                        label={'Datum zaplacení'}
                        dateOnly={false}
                        disabled={true}
                      />
                    </Col>
                  </Row>
                )}
              </>
            )}

          {resolution &&
            resolution === 'BNZ' && (
              <Row>
                <Col md={12}>
                  <h4>Platba</h4>
                </Col>
                <Col md={6}>
                  <RemoteAutoCompleteField
                    component={RemoteAutoComplete}
                    name={'fine.employee'}
                    label="Zaměstnanec"
                    url={`/metropolis/api/hr/employees`}
                    fullWidth={true}
                    labelFunction={o => get(o, 'fullName')}
                    valueFunction={o => o}
                    disabled={true}
                  />
                </Col>
                <Col md={6}>
                  <InputField component={Input} name="fine.fullNumber" label="Doklad" disabled={true} />
                </Col>
                <Col md={4}>
                  <AutoCompleteField
                    component={AutoComplete}
                    name="fine.state"
                    label="Stav dokladu"
                    fullWidth={true}
                    items={ticketState}
                    labelFunction={o => get(o, 'label')}
                    valueFunction={o => get(o, 'id')}
                    disabled={true}
                  />
                </Col>
                <Col md={4}>
                  <InputField component={Input} name={'fine.value'} label={'Suma'} disabled={true} />
                </Col>
                <Col md={4}>
                  <DateField
                    component={Date}
                    name={'fine.useDate'}
                    label={'Datum užití'}
                    dateOnly={false}
                    disabled={true}
                  />
                </Col>
                <Col md={6}>
                  <AutoCompleteField
                    component={AutoComplete}
                    name="fine.paymentMethod"
                    label="Platební metoda"
                    fullWidth={true}
                    items={paymentMethod}
                    labelFunction={o => get(o, 'label')}
                    valueFunction={o => get(o, 'id')}
                    disabled={true}
                  />
                </Col>
              </Row>
            )}

          {resolution &&
            resolution === 'REBUKE' && (
              <Row>
                <Col md={12}>
                  <h4>{'Domluva'}</h4>
                </Col>
                <Col md={12}>
                  <RemoteAutoCompleteField
                    component={RemoteAutoComplete}
                    name={'resolutionOfficer'}
                    label="Zaměstnanec"
                    url={`/metropolis/api/hr/employees`}
                    fullWidth={true}
                    labelFunction={o => get(o, 'fullName')}
                    valueFunction={o => o}
                    disabled={true}
                  />
                </Col>
              </Row>
            )}

          {isepCheckDatetime && (
            <Row>
              <Col md={12}>
                <h4>Opis z ISEP</h4>
              </Col>
              <Col md={12}>
                <DateField
                  component={Date}
                  name={'isepCheckDatetime'}
                  label={'Datum opisu'}
                  dateOnly={false}
                  disabled={true}
                />
              </Col>
            </Row>
          )}

          {isepId && (
            <Row>
              <Col md={12}>
                <h4>Zápis ISEP</h4>
              </Col>
              <Col md={6}>
                <InputField component={Input} name={'isepId'} label={'Identifikátor ISEP'} disabled={true} />
              </Col>
              <Col md={6}>
                <DateField
                  component={Date}
                  name={'isepDate'}
                  label={'Datum zapsání'}
                  dateOnly={false}
                  disabled={true}
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col className="grid-system-margin" />
          </Row>
        </Grid>

        <Divider />

        <div className="buttons" style={{ display: 'flex', justifyContent: 'space-between', margin: '8px' }}>
          <div className="primary-buttons">
            <Button
              label="Uložit"
              primary={true}
              style={{ margin: '8px' }}
              disabled={!dirty}
              onClick={handleSubmit(onSubmit)}
            />
            <Button label="Zrušit" style={{ margin: '8px' }} onClick={closeItem} />
          </div>
          <div className="secondary-buttons" style={{ display: 'flex', flexWrap: 'wrap-reverse' }}>
            {!!resolution && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Button label={'Zvrátit řešení'} onClick={undo} style={{ margin: '8px' }} disabled={!undoPermission} />
                {!undoPermission && <HelpTooltip help="Nemáte oprávnení pro tuhle akci" />}
              </div>
            )}
            {!resolution &&
              [
                {
                  label: 'Skutek se nestal',
                  disabled: buttonOptionalOffender,
                  onClick: () => {
                    if (isResolutionValid()) {
                      handleShowNotHappend(true);
                    }
                  }
                },
                {
                  label: 'Předáno na SO',
                  disabled: buttonOptionalOffender,
                  onClick: () => {
                    if (isResolutionValid()) {
                      handleShowForwardedSo(true);
                    }
                  }
                },
                {
                  label: 'Domluva',
                  disabled: buttonDisabled,
                  onClick: () => {
                    if (isResolutionValid()) {
                      handleShowAgreement(true);
                    }
                  }
                },
                {
                  label: 'Zaplatit pokutu',
                  disabled: buttonDisabled,
                  onClick: () => {
                    if (isResolutionValid()) {
                      handleShowPayment(true);
                    }
                  }
                },
                {
                  label: 'Vydat BNP',
                  disabled: buttonDisabled,
                  onClick: () => {
                    if (isResolutionValid()) {
                      handleShowBNP(true);
                    }
                  }
                }
              ].map(buttonProps => <Button key={buttonProps.label} style={{ margin: '8px' }} {...buttonProps} />)}
            {resolution === 'BNP' &&
              (fineState === 'USED_BNP' || fineState === 'DEMAND_BNP') && (
                <>
                  <Button label={'Zaplatit BNP'} onClick={() => handleShowBNPPayment(true)} style={{ margin: '8px' }} />
                  <Button
                    label={'Platba přes BNZ'}
                    onClick={() => handleShowBNZPayment(true)}
                    style={{ margin: '8px' }}
                  />
                </>
              )}
            {!isepId &&
              isepCheckDatetime && (
                <Button
                  label={'Zápis ISEP'}
                  onClick={() => handleShowSaveISEP(true)}
                  style={{ margin: '8px' }}
                  disabled={buttonDisabled}
                />
              )}
            <Button
              label={'Opis z ISEP'}
              onClick={handleIsepFile}
              style={{ margin: '8px' }}
              disabled={buttonDisabled}
            />
            <Button
              label={'Opis z ISRT'}
              onClick={handleIsrtFile}
              style={{ margin: '8px' }}
              disabled={buttonDisabled}
            />
            {buttonDisabled && (
              <HelpTooltip
                help={`Pro vyřešení přestupku potřebujete${
                  !buttonOptionalOffender ? ' vyplnit pachatele a' : ''
                } uložit změny`}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export const EventsFormDelictsDelictsForm = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/events/EventsFormDelictsDelictsForm.tsx